import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import {
  Plus,
  Save,
  Bot,
  Copy,
  Check,
  Settings as SettingsIcon,
  LogIn,
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Toaster, toast } from 'sonner';
import FileList from './components/FileList';
import CreateFileModal from './components/CreateFileModal';
import AuthModal from './components/AuthModal';
import SettingsPanel from './components/SettingsPanel';
import AISidebar from './components/AISidebar';

import { File, Folder } from './types';
import { formatCode, supportsFormatting, basicFormat } from './lib/formatter';

const App: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [filesLoading, setFilesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorFontSize, setEditorFontSize] = useState<number>(14);
  const [editorTheme, setEditorTheme] = useState<'vs' | 'vs-dark' | 'hc-black'>(
    'vs-dark'
  );
  const [formatOnSave, setFormatOnSave] = useState<boolean>(() => {
    const saved = localStorage.getItem('formatOnSave');
    return saved ? JSON.parse(saved) : true; // Default to true
  });
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem('sidebarWidth'));
    if (saved && !Number.isNaN(saved))
      return Math.min(Math.max(saved, 200), 480);
    return 256; // default 16rem
  });
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [aiWidth, setAiWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem('aiWidth'));
    return !Number.isNaN(saved) && saved
      ? Math.min(Math.max(saved, 280), 700)
      : 360;
  });
  // toasts replace banner states
  const [copied, setCopied] = useState(false);
  const [withScriptTag, setWithScriptTag] = useState(false);
  const [authOpen, setAuthOpen] = useState<boolean>(
    !localStorage.getItem('sessionId')
  );
  const [sessionId, setSessionId] = useState<string | null>(
    localStorage.getItem('sessionId')
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(
    localStorage.getItem('userEmail')
  );
  const [theme, setTheme] = useState<'light' | 'dark'>(
    (localStorage.getItem('theme') as 'light' | 'dark') || 'dark'
  );

  useEffect(() => {
    fetchFiles();
  }, [sessionId]); // Refetch files when sessionId changes

  useEffect(() => {
    const syncUser = async () => {
      if (!sessionId) return;
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: 'Bearer ' + sessionId },
        });
        const raw = await res.text();
        let data: any = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          data = {};
        }
        const t = data?.user?.user_metadata?.theme as
          | 'light'
          | 'dark'
          | undefined;
        if (t === 'light' || t === 'dark') setTheme(t);
        const editorMeta = data?.user?.user_metadata?.editor;
        if (editorMeta?.fontSize && typeof editorMeta.fontSize === 'number')
          setEditorFontSize(editorMeta.fontSize);
        if (
          editorMeta?.theme &&
          ['vs', 'vs-dark', 'hc-black'].includes(editorMeta.theme)
        )
          setEditorTheme(editorMeta.theme);
        if (typeof editorMeta?.formatOnSave === 'boolean')
          setFormatOnSave(editorMeta.formatOnSave);
        if (data?.user?.email) {
          setUserEmail(data.user.email);
          localStorage.setItem('userEmail', data.user.email);
        }
      } catch {
        // ignore
      }
    };
    syncUser();
  }, [sessionId]);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    // Persist locally only for guests. When authenticated, Supabase user metadata is the source of truth.
    if (!sessionId) {
      localStorage.setItem('theme', theme);
    } else {
      localStorage.removeItem('theme');
    }
  }, [theme, sessionId]);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const next = Math.min(Math.max(e.clientX, 200), 480);
      setSidebarWidth(next);
    };
    const onUp = () => {
      setIsResizing(false);
      localStorage.setItem('sidebarWidth', String(sidebarWidth));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizing, sidebarWidth]);

  // Persist formatOnSave setting
  useEffect(() => {
    localStorage.setItem('formatOnSave', JSON.stringify(formatOnSave));
  }, [formatOnSave]);

  const fetchFiles = async () => {
    try {
      setFilesLoading(true);
      const response = await fetch('/api/files');
      const data = await response.json();
      if (Array.isArray(data)) {
        setFiles(data);
      } else {
        setFiles(data.files || []);
        setFolders(data.folders || []);
      }
    } catch (error) {
      toast.error('Failed to fetch files');
    } finally {
      setFilesLoading(false);
    }
  };

  const createFile = async (name: string, language: string) => {
    try {
      const response = await fetch('/api/files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { Authorization: 'Bearer ' + sessionId } : {}),
        },
        body: JSON.stringify({ name, content: '', language }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to create file');
      }

      const newFile = await response.json();
      setFiles([newFile, ...files]);
      setCurrentFile(newFile);
      setEditorContent('');
      setShowCreateModal(false);
      toast.success('File created');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create file');
    } finally {
      // no list loading state here; keep sidebar visible
    }
  };

  const saveFile = async () => {
    if (!currentFile) return;

    try {
      setSaving(true);

      let contentToSave = editorContent;

      // Format code if formatOnSave is enabled
      if (formatOnSave && editorContent.trim()) {
        const language = getLanguageFromExtension(currentFile.name);
        console.log('Attempting to format:', { language, contentLength: editorContent.length });

        try {
          if (supportsFormatting(language)) {
            console.log('Using formatCode for language:', language);
            contentToSave = await formatCode(editorContent, language);
          } else {
            console.log('Using basicFormat for language:', language);
            contentToSave = basicFormat(editorContent, language);
          }

          // Update the editor content with formatted code
          if (contentToSave !== editorContent) {
            console.log('Content changed after formatting, updating editor');
            setEditorContent(contentToSave);
          } else {
            console.log('Content unchanged after formatting');
          }
        } catch (formatError) {
          console.warn('Failed to format code:', formatError);
          // Continue with original content if formatting fails
        }
      }

      console.log('Saving file with content length:', contentToSave.length);

      const response = await fetch(`/api/files/${currentFile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { Authorization: 'Bearer ' + sessionId } : {}),
        },
        body: JSON.stringify({
          name: currentFile.name,
          content: contentToSave,
          language: currentFile.language,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      toast.success('Saved');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteFile = async (fileId: string) => {
    try {
      await fetch(`/api/files/${fileId}`, {
        method: 'DELETE',
        headers: sessionId ? { Authorization: 'Bearer ' + sessionId } : {},
      });
      setFiles(files.filter(f => f.id !== fileId));
      if (currentFile?.id === fileId) {
        setCurrentFile(null);
        setEditorContent('');
      }
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    } finally {
      // keep sidebar visible
    }
  };

  // Folder actions
  const createFolder = async (parentId?: string | null) => {
    try {
      const res = await fetch('/api/files/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'New Folder',
          parent_id: parentId || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create folder');
      setFolders(prev => [data, ...prev]);
    } catch (err) {
      toast.error('Failed to create folder');
    }
  };

  const renameFolder = async (folderId: string, newName: string) => {
    try {
      const res = await fetch(`/api/files/folders/${folderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      const raw = await res.text();
      let data: any = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {}
      if (!res.ok) throw new Error(data.error || 'Failed to rename folder');
      setFolders(prev =>
        prev.map(f =>
          f.id === folderId
            ? {
                ...f,
                name: newName,
                updated_at: data.updated_at || new Date().toISOString(),
              }
            : f
        )
      );
    } catch (err) {
      toast.error('Failed to rename folder');
    }
  };

  const moveFile = async (fileId: string, folderId: string | null) => {
    try {
      const res = await fetch(`/api/files/${fileId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_id: folderId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to move file');
      setFiles(prev =>
        prev.map(f => (f.id === fileId ? { ...f, folder_id: folderId } : f))
      );
    } catch (err) {
      toast.error('Failed to move file');
    }
  };

  const moveFolder = async (folderId: string, parentId: string | null) => {
    try {
      const res = await fetch(`/api/files/folders/${folderId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_id: parentId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to move folder');
      setFolders(prev =>
        prev.map(f => (f.id === folderId ? { ...f, parent_id: parentId } : f))
      );
    } catch (err) {
      toast.error('Failed to move folder');
    }
  };

  const deleteFolder = async (folderId: string) => {
    try {
      const res = await fetch(`/api/files/folders/${folderId}`, {
        method: 'DELETE',
      });
      const raw = await res.text();
      if (!res.ok) throw new Error(raw || 'Failed to delete folder');
      setFolders(prev => prev.filter(f => f.id !== folderId));
      // Detach any files in this folder locally (server sets NULL)
      setFiles(prev =>
        prev.map(f =>
          f.folder_id === folderId ? { ...f, folder_id: null } : f
        )
      );
    } catch (err) {
      toast.error('Failed to delete folder');
    }
  };

  const renameFile = async (fileId: string, newName: string) => {
    try {
      const res = await fetch(`/api/files/${fileId}/rename`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { Authorization: 'Bearer ' + sessionId } : {}),
        },
        body: JSON.stringify({ name: newName }),
      });
      const raw = await res.text();
      let data: any = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = { error: raw };
      }
      if (!res.ok)
        throw new Error(
          data.error || res.statusText || 'Failed to rename file'
        );
      setFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, name: data.name, updated_at: data.updated_at }
            : f
        )
      );
      if (currentFile?.id === fileId) {
        setCurrentFile(prev =>
          prev ? ({ ...prev, name: data.name } as File) : prev
        );
      }
      toast.success('Renamed');
    } catch (err: any) {
      toast.error(err.message || 'Failed to rename');
    }
  };

  const loadFile = async (file: File) => {
    try {
      setEditorLoading(true);
      const response = await fetch(`/api/files/${file.id}`);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to load file');
      }
      const fileData = await response.json();
      setCurrentFile(fileData);
      setEditorContent(fileData.content || '');
    } catch (error: any) {
      toast.error(error.message || 'Failed to load file');
    } finally {
      setEditorLoading(false);
    }
  };

  const copyPublicLink = async () => {
    if (!currentFile) return;

    const publicUrl = `${window.location.origin}/api/files/public/${currentFile.id}`;
    const textToCopy = withScriptTag
      ? `<script src="${publicUrl}"></script>`
      : publicUrl;

    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (!currentFile) return;
    const timer = setTimeout(() => saveFile(), 2000);
    return () => clearTimeout(timer);
  }, [editorContent]);

  // Add keyboard shortcut for Ctrl+S/Cmd+S to save file
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's default save behavior
        if (currentFile) {
          saveFile(); // Trigger our app's save function
        }
      }
    };

    // Add event listener to the document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentFile]); // Dependencies: re-attach when currentFile changes

  const getLanguageFromExtension = (filename?: string): string => {
    if (!filename) return 'plaintext';
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap: { [key: string]: string } = {
      js: 'javascript',
      ts: 'typescript',
      jsx: 'javascript',
      tsx: 'typescript',
      html: 'html',
      css: 'css',
      json: 'json',
      py: 'python',
      java: 'java',
      cpp: 'cpp',
      c: 'c',
      php: 'php',
      rb: 'ruby',
      go: 'go',
      rs: 'rust',
      sql: 'sql',
      md: 'markdown',
      txt: 'plaintext',
    };
    return languageMap[ext || ''] || 'plaintext';
  };

  const handleLoggedOut = () => {
    localStorage.removeItem('sessionId');
    localStorage.removeItem('userEmail');
    setSessionId(null);
    setUserEmail(null);
    setAuthOpen(true);
  };

  return (
    <div className='h-screen flex flex-col bg-background text-foreground'>
      <Toaster richColors theme='dark' />
      {/* Header */}
      <header className='border-b px-4 py-2 flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className='mr-2 h-4 w-4' /> New File
          </Button>
          {currentFile && (
            <div className='flex items-center gap-2'>
              <Button variant='secondary' onClick={copyPublicLink}>
                {copied ? (
                  <Check className='mr-2 h-4 w-4' />
                ) : (
                  <Copy className='mr-2 h-4 w-4' />
                )}{' '}
                Copy Link
              </Button>
              <label className='flex items-center gap-2 text-sm cursor-pointer'>
                <input
                  type='checkbox'
                  checked={withScriptTag}
                  onChange={e => setWithScriptTag(e.target.checked)}
                  className={`h-4 w-4 rounded border border-input bg-background focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    theme === 'dark' ? 'text-white' : 'text-black'
                  }`}
                />
                <span className='text-foreground font-medium'>
                  with Script tag
                </span>
              </label>
            </div>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <Button
            onClick={saveFile}
            disabled={!currentFile || saving || editorLoading}
          >
            <Save className='mr-2 h-4 w-4' /> Save
          </Button>
          {sessionId ? (
            <Button variant='secondary' onClick={() => setSettingsOpen(true)}>
              <SettingsIcon className='mr-2 h-4 w-4' /> Settings
            </Button>
          ) : (
            <Button variant='secondary' onClick={() => setAuthOpen(true)}>
              <LogIn className='mr-2 h-4 w-4' /> Sign In
            </Button>
          )}
        </div>
      </header>

      {/* Body */}
      <div className='flex flex-1 min-h-0'>
        {/* Sidebar */}
        <aside
          className='border-r p-2 overflow-y-auto select-none'
          style={{ width: sidebarWidth }}
        >
          <FileList
            files={files}
            folders={folders}
            currentFile={currentFile}
            onFileSelect={loadFile}
            onFileDelete={deleteFile}
            onFileRename={renameFile}
            onCreateFolder={createFolder}
            onRenameFolder={renameFolder}
            onMoveFile={moveFile}
            onMoveFolder={moveFolder}
            onDeleteFolder={deleteFolder}
            loading={filesLoading}
          />
        </aside>
        {/* Resizer */}
        <div
          className={`w-1 cursor-col-resize bg-border hover:bg-muted transition-colors ${isResizing ? 'bg-muted' : ''}`}
          onMouseDown={() => setIsResizing(true)}
        />

        {/* Editor */}
        <main className='flex-1 min-w-0 relative'>
          <div className='h-full relative'>
            {currentFile ? (
              <Editor
                height='100%'
                language={getLanguageFromExtension(currentFile.name)}
                value={editorContent}
                onChange={v => setEditorContent(v || '')}
                theme={editorTheme}
                options={{
                  minimap: { enabled: true },
                  fontSize: editorFontSize,
                  wordWrap: 'on',
                  automaticLayout: true,
                  suggestOnTriggerCharacters: true,
                  quickSuggestions: true,
                  parameterHints: { enabled: true },
                  hover: { enabled: true },
                }}
              />
            ) : (
              <div className='h-full grid place-items-center text-muted-foreground text-lg'>
                Select a file to start coding
              </div>
            )}
            {editorLoading && (
              <div className='absolute inset-0 grid place-items-center bg-background/80'>
                <div className='flex items-center gap-3 text-sm text-muted-foreground'>
                  <span className='inline-block h-4 w-4 rounded-full border-2 border-muted border-t-foreground animate-spin' />
                  Loading file…
                </div>
              </div>
            )}
            {/* Sayu Sidebar (new) */}
            {showAIPanel && currentFile && (
              <div
                className='absolute right-0 top-0 h-full pointer-events-auto z-[60]'
                style={{ width: aiWidth }}
              >
                <AISidebar
                  open={showAIPanel}
                  width={aiWidth}
                  onWidthChange={w => {
                    setAiWidth(w);
                    localStorage.setItem('aiWidth', String(w));
                  }}
                  onClose={() => setShowAIPanel(false)}
                  file={currentFile}
                  content={editorContent}
                  onContentUpdate={setEditorContent}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals / Panels */}
      {showCreateModal && (
        <CreateFileModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createFile}
        />
      )}
      {/* legacy AIPanel removed in favor of AISidebar */}
      {authOpen && (
        <AuthModal
          onClose={() => setAuthOpen(false)}
          onAuthSuccess={(sid, email) => {
            setSessionId(sid);
            setUserEmail(email);
            setAuthOpen(false);
          }}
        />
      )}
      {settingsOpen && sessionId && (
        <SettingsPanel
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          sessionId={sessionId}
          currentEmail={userEmail}
          theme={theme}
          onThemeChanged={t => setTheme(t)}
          editorFontSize={editorFontSize}
          editorTheme={editorTheme}
          formatOnSave={formatOnSave}
          onEditorPrefsChanged={({
            fontSize,
            theme: et,
            formatOnSave: fos,
          }) => {
            if (fontSize) setEditorFontSize(fontSize);
            if (et) setEditorTheme(et);
            if (typeof fos === 'boolean') setFormatOnSave(fos);
          }}
          onEmailUpdated={e => setUserEmail(e)}
          onLoggedOut={handleLoggedOut}
        />
      )}

      {/* FAB AI */}
      {sessionId && currentFile && (
        <button
          className='fixed right-6 bottom-10 z-50 h-12 w-12 rounded-full bg-secondary text-secondary-foreground inline-flex items-center justify-center shadow-md hover:bg-secondary/80'
          onClick={() => setShowAIPanel(!showAIPanel)}
          title='Sayu'
          aria-label='Toggle Sayu'
        >
          <Bot size={20} />
        </button>
      )}
    </div>
  );
};

export default App;
